import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Bot, Copy, CheckCircle2, Save, PhoneCall, MessageSquare, XCircle, PlayCircle, Flame } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CopyRow({ label, value, k, copied, onCopy, testid }) {
  return (
    <div>
      <div className="text-white/45 text-xs uppercase tracking-wider mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <code data-testid={testid} className="flex-1 bg-black/50 border border-white/10 rounded-md px-3 py-2 text-xs text-white/80 truncate">{value}</code>
        <button onClick={() => onCopy(value, k)} className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-white/70 hover:text-white text-xs flex items-center gap-1.5 shrink-0">
          {copied === k ? <CheckCircle2 size={13} className="text-green-400" /> : <Copy size={13} />} Copy
        </button>
      </div>
    </div>
  );
}

const FIELDS = [
  { key: "booking_link", label: "Booking Link (where the AI sends people)", rows: 1, ph: "https://www.auto-hirestl.com" },
  { key: "hours", label: "Business Hours", rows: 2 },
  { key: "specials", label: "Current Specials / Promotions", rows: 2, ph: "e.g. Weekend Special — 10% off Fri-Mon rentals booked this week" },
  { key: "insurance_rules", label: "Insurance Rules", rows: 3 },
  { key: "faqs", label: "FAQs (one Q/A per line)", rows: 5 },
];

export default function UpfirstPanel({ token }) {
  const [config, setConfig] = useState(null);
  const [knowledge, setKnowledge] = useState(null);
  const [aiLeads, setAiLeads] = useState([]);
  const [copied, setCopied] = useState("");
  const [saving, setSaving] = useState(false);
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const H = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/leads/intake-config`, H).then((r) => setConfig(r.data)).catch(() => {});
    axios.get(`${API}/marketing/ai-knowledge`, H).then((r) => setKnowledge(r.data)).catch(() => {});
    axios.get(`${API}/leads?source=${encodeURIComponent("Upfirst AI")}`, H).then((r) => setAiLeads(r.data.slice(0, 15))).catch(() => {});
    // eslint-disable-next-line
  }, [token]);

  const copy = (text, key) => { navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1500); };

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    try {
      const { data } = await axios.post(`${API}/leads/upfirst-test`, {}, H);
      setTest(data);
      data.ok ? toast.success("End-to-end pipeline test passed!") : toast.error("Some pipeline steps need attention.");
    } catch { toast.error("Test failed to run."); }
    finally { setTesting(false); }
  };

  const syncGhl = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/leads/sync-ghl`, {}, H);
      toast.success(`GHL backfill done — ${data.synced} synced${data.failed ? `, ${data.failed} failed` : ""}.`);
    } catch { toast.error("Backfill failed to run."); }
    finally { setSyncing(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/marketing/ai-knowledge`, knowledge, H);
      toast.success("AI knowledge saved — Upfirst gets the update instantly.");
    } catch { toast.error("Could not save."); }
    finally { setSaving(false); }
  };

  if (!config || !knowledge) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Loading Upfirst setup…</div>;

  const base = config.endpoint.replace("/api/leads/inbound", "");
  const knowledgeUrl = `${base}/api/ai/knowledge?key=${config.key}`;

  return (
    <div data-testid="upfirst-panel" className="space-y-5">
      <div className="bg-[#0C0C0C] border border-[#F2B705]/25 rounded-xl p-5 space-y-4">
        <h3 className="font-display font-bold text-white flex items-center gap-2"><Bot size={17} className="text-[#F2B705]" /> Upfirst AI — Your 24/7 Sales Rep</h3>
        <CopyRow label="1. Lead / Call / Text Webhook — Upfirst posts every conversation here" value={config.endpoint} k="ep" copied={copied} onCopy={copy} testid="upfirst-webhook-url" />
        <CopyRow label="2. Secret Key — add as X-Lead-Key header (or ?key=… on the URL)" value={config.key} k="key" copied={copied} onCopy={copy} testid="upfirst-key" />
        <CopyRow label="3. Live Knowledge Feed — give Upfirst this URL so it always knows availability, prices & specials" value={knowledgeUrl} k="kn" copied={copied} onCopy={copy} testid="upfirst-knowledge-url" />
        <div className="bg-black/40 border border-white/10 rounded-lg p-4 text-white/60 text-xs leading-relaxed space-y-3">
          <div className="text-white/80 font-semibold text-sm">Setup in the Upfirst dashboard (3 webhooks, same URL + key):</div>
          <div>
            <div className="text-[#F2B705] font-semibold mb-1">A. After every CALL — post:</div>
            <pre className="bg-black/60 border border-white/10 rounded p-2 overflow-x-auto text-[11px] text-white/70">{`{ "name": "Jane Caller", "phone": "+13145551234", "email": "",
  "source": "Upfirst AI", "type": "call", "outcome": "answered",
  "call_summary": "Wants a mid-size SUV Fri-Mon", "call_duration": "3m 42s",
  "appointment": false }`}</pre>
          </div>
          <div>
            <div className="text-[#F2B705] font-semibold mb-1">B. After every TEXT conversation — post:</div>
            <pre className="bg-black/60 border border-white/10 rounded p-2 overflow-x-auto text-[11px] text-white/70">{`{ "name": "Mike Texter", "phone": "+13145559876",
  "source": "Upfirst AI", "type": "text",
  "message": "Asked about weekly rates on the Malibu" }`}</pre>
          </div>
          <div>
            <div className="text-[#F2B705] font-semibold mb-1 flex items-center gap-1"><Flame size={12} /> C. HOT LEAD / live transfer — add <code className="text-white">"hot": true</code>:</div>
            <pre className="bg-black/60 border border-white/10 rounded p-2 overflow-x-auto text-[11px] text-white/70">{`{ "name": "Ready Buyer", "phone": "+13145551111",
  "source": "Upfirst AI", "type": "call", "hot": true,
  "call_summary": "Wants to book TODAY, has license + insurance ready" }`}</pre>
            <div className="mt-1">→ You instantly get a 🔥 text + email so you can call them back or take the transfer. Tell Upfirst's AI: "when the caller is ready to book or asks for a human, mark the lead hot and offer to transfer to {`{your number}`}."</div>
          </div>
          <div>
            <div className="text-white/80 font-semibold">D. Knowledge:</div>
            Add URL #3 as a knowledge source / API tool — live fleet, prices, unavailable dates, hours, insurance rules, specials + booking link as JSON. Tell the AI to send callers the booking link to finish reservations.
          </div>
          <div className="text-white/50">Every conversation lands in the Leads Inbox tagged "Upfirst AI", pushes to GoHighLevel automatically, and enters the Autopilot follow-up sequence (email — or SMS via GHL when there's only a phone number).</div>
        </div>

        <div className="border-t border-white/10 pt-4">
          <div className="flex flex-wrap gap-3">
            <button data-testid="upfirst-e2e-test-btn" onClick={runTest} disabled={testing}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#F2B705] text-black font-bold text-sm disabled:opacity-50">
              {testing ? <Loader2 size={15} className="animate-spin" /> : <PlayCircle size={15} />} {testing ? "Testing pipeline…" : "Run End-to-End Test"}
            </button>
            <button data-testid="upfirst-ghl-backfill-btn" onClick={syncGhl} disabled={syncing}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-white/5 border border-white/15 text-white/80 hover:text-white font-semibold text-sm disabled:opacity-50">
              {syncing ? <Loader2 size={15} className="animate-spin" /> : <Bot size={15} />} {syncing ? "Syncing…" : "Sync Missing Leads → GHL"}
            </button>
          </div>
          {test && (
            <div data-testid="upfirst-e2e-results" className="mt-4 space-y-2">
              {test.checks.map((c, i) => (
                <div key={i} className="flex items-start gap-2.5 bg-black/30 border border-white/5 rounded-lg px-3 py-2.5">
                  {c.ok ? <CheckCircle2 size={16} className="text-green-400 mt-0.5 shrink-0" /> : <XCircle size={16} className="text-red-400 mt-0.5 shrink-0" />}
                  <div>
                    <div className="text-white text-sm font-semibold">{c.step}</div>
                    <div className="text-white/50 text-xs">{c.detail}</div>
                  </div>
                </div>
              ))}
              <div className="text-white/40 text-xs">{test.note}</div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5">
        <h3 className="font-display font-bold text-white mb-1">What the AI Knows</h3>
        <p className="text-white/45 text-sm mb-4">Edit anytime — the knowledge feed updates instantly. Vehicle availability and prices are pulled live from your fleet automatically.</p>
        <div className="space-y-4">
          {FIELDS.map((f) => (
            <label key={f.key} className="block text-xs text-white/50 uppercase tracking-wider">
              {f.label}
              <textarea data-testid={`knowledge-${f.key}`} rows={f.rows} value={knowledge[f.key] || ""} placeholder={f.ph || ""}
                onChange={(e) => setKnowledge({ ...knowledge, [f.key]: e.target.value })}
                className="mt-1 w-full bg-black/40 border border-white/10 focus:border-[#F2B705] rounded-md px-3 py-2 text-white text-sm outline-none normal-case resize-y" />
            </label>
          ))}
          <button data-testid="knowledge-save-btn" onClick={save} disabled={saving} className="flex items-center gap-1.5 px-5 py-2 rounded-md bg-[#F2B705] text-black font-bold text-sm disabled:opacity-50">
            <Save size={14} /> {saving ? "Saving…" : "Save AI Knowledge"}
          </button>
        </div>
      </div>

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5">
        <h3 className="font-display font-bold text-white mb-3">Recent AI Conversations</h3>
        <div className="space-y-2.5">
          {aiLeads.map((l) => (
            <div key={l.id} className="flex items-start gap-3 bg-black/30 border border-white/5 rounded-lg px-4 py-3">
              {(l.contact_type === "text" || l.contact_type === "sms" || l.contact_type === "chat")
                ? <MessageSquare size={16} className="text-blue-300 mt-0.5 shrink-0" />
                : <PhoneCall size={16} className="text-[#F2B705] mt-0.5 shrink-0" />}
              <div className="min-w-0">
                <div className="text-white text-sm font-semibold">{l.name} <span className="text-white/40 font-normal text-xs">· {[l.phone, l.email].filter(Boolean).join(" · ")}</span></div>
                {l.call_summary && <div className="text-white/60 text-xs mt-0.5">{l.call_summary}{l.call_duration ? ` · ${l.call_duration}` : ""}</div>}
                {l.message && !l.call_summary && <div className="text-white/60 text-xs mt-0.5">{l.message}</div>}
              </div>
              <div className="ml-auto text-white/35 text-xs whitespace-nowrap">{fmtDateTime(l.created_at)}</div>
            </div>
          ))}
          {aiLeads.length === 0 && <div className="text-white/40 text-sm py-4 text-center">No AI conversations yet — they'll appear here the moment Upfirst is connected.</div>}
        </div>
      </div>
    </div>
  );
}
